// ============================================================================
// FudiTap — Restaurant Tenant Config (single source of truth)
// The real FudiTap API can populate this exact shape per restaurant/branch
// without any UI changes. Menu content lives in src/data/menu.js.
// ============================================================================

import { isApiMenuSource } from "@/config/menuSource";

export const PRODUCT_MODES = { FULL_MENU: "FULL_MENU", COMPANION_ONLY: "COMPANION_ONLY" };

// Read at call time (not import time) so a tenant config swap takes effect.
export function isFullMenuMode(config = restaurantConfig) {
  return config.mode !== PRODUCT_MODES.COMPANION_ONLY;
}

export const restaurantConfig = {
  id: "rest_fudi_kitchen",

  // "FULL_MENU" = full FudiTap digital menu. "COMPANION_ONLY" = restaurant keeps
  // its own external menu; FudiTap provides services + a "Menüyü Aç" link.
  mode: PRODUCT_MODES.FULL_MENU,

  // --- Identity ---
  name: "Fudi Kitchen",
  tagline: {
    tr: "Taze malzemeler, modern mutfak",
    en: "Fresh ingredients, modern kitchen",
    de: "Frische Zutaten, moderne Küche",
    fr: "Produits frais, cuisine moderne",
    ru: "Свежие продукты, современная кухня",
    ar: "مكونات طازجة ومطبخ عصري",
  },
  logoImage: null, // optional restaurant logo (transparent); falls back to mark+name

  branch: { id: "branch_talas", name: "Talas", city: "Kayseri" },

  // --- Status / service ---
  isOpen: true,
  hours: "08:00 – 23:30",
  avgPrepTimeMin: 18,
  prepRangeMin: [15, 20],
  kitchenLoad: "calm", // "calm" | "busy"  (mock KDS signal)
  rating: 4.8,
  reviewCount: 1240,

  // --- Locale / currency ---
  currency: "TRY",
  currencyLocale: "tr-TR",

  // --- Imagery ---
  coverImage:
    "https://images.unsplash.com/photo-1583354608715-177553a4035e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",

  // --- External business links (mock URLs for prototype) ---
  links: {
    externalMenuUrl: "https://example.com/fudi-kitchen-menu",
    googleReviewUrl: "https://g.page/r/fudi-kitchen/review",
    instagramUrl: "https://instagram.com/fudikitchen",
    locationUrl: "https://maps.google.com/?q=Fudi+Kitchen+Talas+Kayseri",
    phone: "+90 352 000 00 00",
    website: "https://example.com/fudi-kitchen",
  },

  // --- Brand theme (applied to CSS variables at runtime) ---
  brand: {
    light: { primary: "24 95% 53%", from: "#F97316", to: "#FB7185" },
    dark: { primary: "24 95% 55%", from: "#F97316", to: "#FB7185" },
  },

  // --- Featured products (ids from src/data/menu.js). Empty => popular. ---
  featuredIds: [
    "p_fudi_burger", "p_serpme", "p_pepperoni", "p_double_burger", "p_pesto", "p_baklava", "p_latte",
  ],

  comboIds: ["combo_burger_menu", "combo_pizza_duo"],

  // --- Promotions (hero; carousel-ready) ---
  promotions: [
    {
      id: "promo_coffee",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
      tag: { tr: "Günün Fırsatı", en: "Today's Deal", de: "Tagesangebot", ar: "عرض اليوم", ru: "Акция дня", fr: "Offre du jour" },
      title: { tr: "2. Kahve %50 İndirimli", en: "2nd Coffee 50% Off", de: "2. Kaffee 50% Rabatt", ar: "القهوة الثانية بخصم 50%", ru: "2-й кофе −50%", fr: "2e café −50%" },
      subtitle: { tr: "Her gün 15:00 – 18:00 arası geçerli", en: "Valid daily 3 – 6 PM", de: "Täglich 15 – 18 Uhr", ar: "يوميًا 3 – 6 مساءً", ru: "Ежедневно 15:00–18:00", fr: "Tous les jours 15h–18h" },
    },
  ],
};

export const platformBranding = { name: "FudiTap", logoImage: "/fuditap-logo.webp" };

// Mock staff assignment for the table session (future: ServiceAssignment API).
export const serviceStaff = {
  id: "staff_ayse",
  firstName: "Ayşe",
  initial: "A",
  role: { tr: "Servis Ekibi", en: "Service Team", de: "Serviceteam", ar: "فريق الخدمة", ru: "Команда сервиса", fr: "Équipe de service" },
};

// Business-configurable loyalty campaign (future: LoyaltyCampaign API).
export const loyaltyCampaign = {
  id: "loyalty_coffee_5",
  enabled: true,
  title: { tr: "5 Siparişe 1 Kahve Bizden ☕", en: "5 Orders = 1 Free Coffee ☕", de: "5 Bestellungen = 1 Gratis-Kaffee ☕", ar: "5 طلبات = قهوة مجانية ☕", ru: "5 заказов = кофе в подарок ☕", fr: "5 commandes = 1 café offert ☕" },
  description: { tr: "Her tamamlanan siparişte bir adım daha yaklaş.", en: "Get closer with every completed order.", de: "Mit jeder Bestellung näher dran.", ar: "اقترب مع كل طلب مكتمل.", ru: "Ближе с каждым заказом.", fr: "Plus proche à chaque commande." },
  threshold: 5,
  // "completed_order": each COMPLETED eligible order counts once.
  // "visit": at most one count per table session, on its first COMPLETED order.
  qualifyingEvent: "completed_order",
  rewardType: "free_product", // "free_product" | "discount" | "custom"
  rewardProductId: "p_turk_kahve", // used when rewardType === "free_product"
  discount: null, // e.g. { kind: "percent", value: 10 } | { kind: "amount", value: 50 }
  customReward: null, // localized text, used when rewardType === "custom"
  branchScope: "branch", // "branch" | "selected" | "organization"
  branchIds: ["branch_talas"],
  startDate: null, // ISO date (inclusive) or null
  endDate: null, // ISO date (inclusive) or null
  terms: { tr: "Yalnızca tamamlanan uygun siparişler sayılır.", en: "Only completed eligible orders count.", de: "Nur abgeschlossene berechtigte Bestellungen zählen.", ar: "تُحتسب الطلبات المكتملة المؤهلة فقط.", ru: "Засчитываются только выполненные заказы.", fr: "Seules les commandes éligibles terminées comptent." },
};

// Resolve table from an NFC/QR slug like `demo-table-12`.
export function resolveTableFromSlug(slug) {
  if (!slug) return null;
  // Real (API) menu: the slug is a card code and there is no table model yet.
  if (isApiMenuSource()) {
    return { slug, tableId: null, tableNumber: null, restaurantId: restaurantConfig.id, branchId: restaurantConfig.branch.id };
  }
  const match = String(slug).match(/(\d+)/);
  const number = match ? parseInt(match[1], 10) : 12;
  return {
    slug,
    tableId: `table_${number}`,
    tableNumber: number,
    restaurantId: restaurantConfig.id,
    branchId: restaurantConfig.branch.id,
  };
}
